import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ClipboardList,
  Columns3,
  FileText,
  Loader2,
  MessageSquare,
  MoreHorizontal,
  Plus,
  RefreshCcw,
  Search,
  UserRound,
  X,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FilterMenu } from "@/components/ui/filter-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDebounce } from "@/hooks/use-debounce";
import { pingAPI } from "@/lib/ping-api";
import { cn } from "@/lib/utils";
import type { User } from "@/types";
import { useAuthStore } from "@/stores/authStore";
import { CONSOLE_PERMISSIONS } from "@/constants/permissions";

type GenderFilter = "all" | "male" | "female" | "other" | "unknown";
type ContactFilter = "all" | "email" | "phone" | "both" | "missing";
type SortField = "name" | "city" | "age";
type SortDirection = "asc" | "desc";
type PatientTab = "overview" | "profile-intake" | "patient-records" | "comms";
type DirectoryColumn = "patientNumber" | "contact" | "city" | "age" | "gender";

const ALL_COLUMNS: Array<{ key: DirectoryColumn; label: string }> = [
  { key: "patientNumber", label: "Patient#" },
  { key: "contact", label: "Contact" },
  { key: "city", label: "City" },
  { key: "age", label: "Age" },
  { key: "gender", label: "Gender" },
];

const GENDER_OPTIONS: Array<{ value: GenderFilter; label: string }> = [
  { value: "all", label: "All genders" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
  { value: "unknown", label: "Not set" },
];

const CONTACT_OPTIONS: Array<{ value: ContactFilter; label: string }> = [
  { value: "all", label: "All contact" },
  { value: "email", label: "Email only" },
  { value: "phone", label: "Phone only" },
  { value: "both", label: "Email and phone" },
  { value: "missing", label: "Missing both" },
];

const getPatientName = (user: User) =>
  `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
  "Unknown patient";

const normalizeSearchText = (value?: string | null) =>
  value?.trim().toLowerCase() ?? "";

const compactSearchText = (value?: string | null) =>
  normalizeSearchText(value).replace(/[^a-z0-9]/g, "");

const getPatientInitials = (user: User) => {
  const initials =
    `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase();
  return initials || "P";
};

const matchesPatientSearch = (user: User, query: string) => {
  const normalizedQuery = normalizeSearchText(query);

  if (!normalizedQuery) return true;

  const patientNumber = normalizeSearchText(user.zarity_user_id);
  const compactQuery = compactSearchText(query);
  const searchTerms = [
    getPatientName(user),
    user.first_name,
    user.last_name,
    user.email,
    user.phone_number,
    user.city,
    patientNumber,
    patientNumber ? `patient#${patientNumber}` : "",
    patientNumber ? `patient ${patientNumber}` : "",
    patientNumber ? `patient${patientNumber}` : "",
  ]
    .map(normalizeSearchText)
    .filter(Boolean);

  if (searchTerms.some((term) => term.includes(normalizedQuery))) {
    return true;
  }

  if (!compactQuery) {
    return false;
  }

  return searchTerms.some((term) =>
    compactSearchText(term).includes(compactQuery)
  );
};

const normalizeGender = (value?: string | null): GenderFilter => {
  const normalized = value?.trim().toLowerCase();

  if (!normalized) return "unknown";
  if (normalized === "m" || normalized === "male") return "male";
  if (normalized === "f" || normalized === "female") return "female";
  if (normalized === "o" || normalized === "other") return "other";

  return "unknown";
};

const formatGender = (value?: string | null) => {
  const normalized = normalizeGender(value);

  if (normalized === "male") return "Male";
  if (normalized === "female") return "Female";
  if (normalized === "other") return "Other";

  return "Not set";
};

const hasEmail = (user: User) => Boolean(user.email?.trim());
const hasPhone = (user: User) => Boolean(user.phone_number?.trim());

const maskPhoneNumber = (value?: string | null) => {
  if (!value) return "Phone not set";

  const digits = value.replace(/\D/g, "");
  if (digits.length <= 4) return value;

  const prefix = digits.slice(0, Math.min(2, digits.length - 4));
  const suffix = digits.slice(-4);
  const hiddenLength = Math.max(
    digits.length - prefix.length - suffix.length,
    0
  );
  const masked = `${prefix}${"*".repeat(hiddenLength)}${suffix}`;

  return value.trim().startsWith("+") ? `+${masked}` : masked;
};

const maskEmail = (value?: string | null) => {
  if (!value) return "Email not set";

  const [localPart, domain] = value.split("@");
  if (!localPart || !domain) return value;
  if (localPart.length <= 2) return `${localPart[0] || "*"}*@${domain}`;

  const visibleStart = localPart.slice(0, 2);
  const visibleEnd = localPart.length > 4 ? localPart.slice(-1) : "";
  const hiddenLength = Math.max(
    localPart.length - visibleStart.length - visibleEnd.length,
    2
  );

  return `${visibleStart}${"*".repeat(hiddenLength)}${visibleEnd}@${domain}`;
};

const sortUsers = (
  users: User[],
  sortField: SortField,
  sortDirection: SortDirection
) => {
  const sortedUsers = [...users].sort((left, right) => {
    if (sortField === "age") {
      const leftAge = left.age ?? -1;
      const rightAge = right.age ?? -1;

      if (leftAge !== rightAge) {
        return leftAge - rightAge;
      }

      return getPatientName(left).localeCompare(getPatientName(right));
    }

    if (sortField === "city") {
      const cityCompare = (left.city || "").localeCompare(right.city || "");
      if (cityCompare !== 0) {
        return cityCompare;
      }

      return getPatientName(left).localeCompare(getPatientName(right));
    }

    return getPatientName(left).localeCompare(getPatientName(right));
  });

  return sortDirection === "desc" ? sortedUsers.reverse() : sortedUsers;
};

const DirectoryLoadingState = ({
  visibleColumns,
}: {
  visibleColumns: Set<DirectoryColumn>;
}) => (
  <div className="overflow-hidden rounded-3xl border border-border/60 bg-card shadow-sm">
    <Table>
      <TableHeader>
        <TableRow className="bg-card hover:bg-transparent">
          <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
            Patient
          </TableHead>
          {visibleColumns.has("patientNumber") ? (
            <TableHead className="hidden md:table-cell z-label uppercase tracking-wide text-muted-foreground">
              Patient#
            </TableHead>
          ) : null}
          {visibleColumns.has("contact") ? (
            <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
              Contact
            </TableHead>
          ) : null}
          {visibleColumns.has("city") ? (
            <TableHead className="hidden lg:table-cell z-label uppercase tracking-wide text-muted-foreground">
              City
            </TableHead>
          ) : null}
          {visibleColumns.has("age") ? (
            <TableHead className="hidden xl:table-cell z-label uppercase tracking-wide text-muted-foreground">
              Age
            </TableHead>
          ) : null}
          {visibleColumns.has("gender") ? (
            <TableHead className="hidden xl:table-cell z-label uppercase tracking-wide text-muted-foreground">
              Gender
            </TableHead>
          ) : null}
          <TableHead className="w-[72px]" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 8 }).map((_, index) => (
          <TableRow key={index}>
            <TableCell>
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-2xl" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
            </TableCell>
            {visibleColumns.has("patientNumber") ? (
              <TableCell className="hidden md:table-cell">
                <Skeleton className="h-4 w-24" />
              </TableCell>
            ) : null}
            {visibleColumns.has("contact") ? (
              <TableCell>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-44" />
                  <Skeleton className="h-3 w-28" />
                </div>
              </TableCell>
            ) : null}
            {visibleColumns.has("city") ? (
              <TableCell className="hidden lg:table-cell">
                <Skeleton className="h-4 w-24" />
              </TableCell>
            ) : null}
            {visibleColumns.has("age") ? (
              <TableCell className="hidden xl:table-cell">
                <Skeleton className="h-4 w-12" />
              </TableCell>
            ) : null}
            {visibleColumns.has("gender") ? (
              <TableCell className="hidden xl:table-cell">
                <Skeleton className="h-4 w-16" />
              </TableCell>
            ) : null}
            <TableCell>
              <Skeleton className="ml-auto h-8 w-8 rounded-md" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

const SortButton = ({
  label,
  isActive,
  direction,
  onClick,
  className,
}: {
  label: string;
  isActive: boolean;
  direction: SortDirection;
  onClick: () => void;
  className?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "inline-flex items-center gap-1.5 text-left z-label uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
  >
    <span>{label}</span>
    {isActive ? (
      direction === "asc" ? (
        <ArrowUp className="h-3.5 w-3.5" />
      ) : (
        <ArrowDown className="h-3.5 w-3.5" />
      )
    ) : (
      <ArrowUpDown className="h-3.5 w-3.5 opacity-60" />
    )}
  </button>
);

export const PatientsDirectory = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuthStore();
  const canCreatePatients = hasPermission(CONSOLE_PERMISSIONS.MANAGE_USERS);
  const [searchQuery, setSearchQuery] = useState("");
  const [genderFilter, setGenderFilter] = useState<GenderFilter>("all");
  const [contactFilter, setContactFilter] = useState<ContactFilter>("all");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [visibleColumns, setVisibleColumns] = useState<Set<DirectoryColumn>>(
    () => new Set(["patientNumber", "contact", "city", "age", "gender"])
  );
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const {
    data: users = [],
    error,
    isFetching,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["patients-directory"],
    queryFn: () => pingAPI.users.searchPatients(""),
  });

  const filteredUsers = useMemo(() => {
    const visibleUsers = users.filter((user) => {
      const matchesSearch = matchesPatientSearch(user, debouncedSearchQuery);
      const matchesGender =
        genderFilter === "all" || normalizeGender(user.gender) === genderFilter;
      const matchesContact =
        contactFilter === "all" ||
        (contactFilter === "email" && hasEmail(user)) ||
        (contactFilter === "phone" && hasPhone(user)) ||
        (contactFilter === "both" && hasEmail(user) && hasPhone(user)) ||
        (contactFilter === "missing" && !hasEmail(user) && !hasPhone(user));

      return matchesSearch && matchesGender && matchesContact;
    });

    return sortUsers(visibleUsers, sortField, sortDirection);
  }, [
    contactFilter,
    debouncedSearchQuery,
    genderFilter,
    sortDirection,
    sortField,
    users,
  ]);

  const hasActiveFilters = genderFilter !== "all" || contactFilter !== "all";

  const toggleColumn = (column: DirectoryColumn) => {
    setVisibleColumns((current) => {
      const next = new Set(current);

      if (next.has(column)) {
        if (next.size === 1) return current;
        next.delete(column);
        return next;
      }

      next.add(column);
      return next;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortField(field);
    setSortDirection("asc");
  };

  const resetFilters = () => {
    setGenderFilter("all");
    setContactFilter("all");
  };

  const openPatient = (patientId: string, tab: PatientTab = "overview") => {
    navigate(`/patients/${patientId}/${tab}`);
  };

  const emptyTitle =
    searchQuery || hasActiveFilters ? "No patients found" : "No patients yet";
  const emptyDescription =
    searchQuery || hasActiveFilters
      ? "Try a broader search or clear one of the active filters."
      : "Add a patient to start building the directory.";

  return (
    <div className="flex h-full flex-col gap-4">
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-3xl border-border/60">
        <CardHeader className="gap-4 border-b border-border/60 bg-card p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className="z-h1 text-foreground">Patients</CardTitle>
              <Button
                variant="outline"
                size="icon"
                onClick={() => refetch()}
                className="h-9 w-9 rounded-xl border-border/70 bg-background/80 shadow-none"
                aria-label="Refresh patients"
              >
                {isFetching && !isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCcw className="h-4 w-4" />
                )}
              </Button>
            </div>

            {canCreatePatients ? (
              <Button
                onClick={() => navigate("/patients/create")}
                className="h-10 gap-2 rounded-xl px-4 shadow-none z-button"
              >
                <Plus className="h-4 w-4" />
                Create New
              </Button>
            ) : null}
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
              <div className="relative w-full md:w-[320px] lg:w-[280px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search patients..."
                  className="h-10 rounded-xl border-border/70 bg-background/85 pl-9 shadow-none z-body"
                />
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <FilterMenu
                  label="Gender"
                  options={GENDER_OPTIONS}
                  value={genderFilter}
                  onValueChange={(value) =>
                    setGenderFilter(value as GenderFilter)
                  }
                  triggerClassName="border-border/70 bg-background px-3 shadow-none hover:bg-accent/20"
                  contentClassName="border-border/70 bg-popover shadow-md"
                />

                <FilterMenu
                  label="Contact"
                  options={CONTACT_OPTIONS}
                  value={contactFilter}
                  onValueChange={(value) =>
                    setContactFilter(value as ContactFilter)
                  }
                  triggerClassName="border-border/70 bg-background px-3 shadow-none hover:bg-accent/20"
                  contentClassName="border-border/70 bg-popover shadow-md"
                />

                {hasActiveFilters ? (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={resetFilters}
                    className="h-9 w-9 rounded-xl border-border/70 bg-background/80 text-muted-foreground shadow-none hover:text-foreground"
                    title="Clear filters"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="shrink-0 lg:ml-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10 gap-2 rounded-xl border-border/70 bg-background/80 px-3 shadow-none"
                  >
                    <Columns3 className="h-4 w-4" />
                    Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  {ALL_COLUMNS.map((column) => (
                    <DropdownMenuCheckboxItem
                      key={column.key}
                      checked={visibleColumns.has(column.key)}
                      onCheckedChange={() => toggleColumn(column.key)}
                    >
                      {column.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col p-0">
          {isLoading ? (
            <div className="p-4">
              <DirectoryLoadingState visibleColumns={visibleColumns} />
            </div>
          ) : error ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <RefreshCcw className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <h2 className="text-base font-semibold text-foreground">
                  Unable to load patients
                </h2>
                <p className="text-sm text-muted-foreground">
                  There was a problem fetching the patient directory.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => refetch()}
                className="z-button"
              >
                Try again
              </Button>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <UserRound className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <h2 className="z-h3 text-foreground">{emptyTitle}</h2>
                <p className="max-w-sm z-secondary text-muted-foreground">
                  {emptyDescription}
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2">
                {hasActiveFilters ? (
                  <Button
                    variant="outline"
                    onClick={resetFilters}
                    className="z-button"
                  >
                    Clear filters
                  </Button>
                ) : null}
                {!searchQuery && canCreatePatients ? (
                  <Button
                    onClick={() => navigate("/patients/create")}
                    className="z-button"
                  >
                    <Plus className="h-4 w-4" />
                    Create New
                  </Button>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between border-b border-border/60 bg-card px-5 py-3 z-secondary text-muted-foreground">
                <p>
                  Showing{" "}
                  <span className="z-numeric font-medium text-foreground">
                    {filteredUsers.length}
                  </span>{" "}
                  of{" "}
                  <span className="z-numeric font-medium text-foreground">
                    {users.length}
                  </span>{" "}
                  patient{users.length === 1 ? "" : "s"}
                </p>
              </div>

              <div className="min-h-0 flex-1 overflow-auto p-4">
                <div className="overflow-hidden rounded-3xl border border-border/60 bg-card shadow-sm">
                  <Table>
                    <TableHeader className="sticky top-0 z-10 bg-card">
                      <TableRow className="hover:bg-card">
                        <TableHead className="min-w-[280px]">
                          <SortButton
                            label="Patient"
                            isActive={sortField === "name"}
                            direction={sortDirection}
                            onClick={() => handleSort("name")}
                          />
                        </TableHead>
                        {visibleColumns.has("patientNumber") ? (
                          <TableHead className="hidden md:table-cell z-label uppercase tracking-wide text-muted-foreground">
                            Patient#
                          </TableHead>
                        ) : null}
                        {visibleColumns.has("contact") ? (
                          <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                            Contact
                          </TableHead>
                        ) : null}
                        {visibleColumns.has("city") ? (
                          <TableHead className="hidden lg:table-cell">
                            <SortButton
                              label="City"
                              isActive={sortField === "city"}
                              direction={sortDirection}
                              onClick={() => handleSort("city")}
                            />
                          </TableHead>
                        ) : null}
                        {visibleColumns.has("age") ? (
                          <TableHead className="hidden xl:table-cell">
                            <SortButton
                              label="Age"
                              isActive={sortField === "age"}
                              direction={sortDirection}
                              onClick={() => handleSort("age")}
                            />
                          </TableHead>
                        ) : null}
                        {visibleColumns.has("gender") ? (
                          <TableHead className="hidden xl:table-cell z-label uppercase tracking-wide text-muted-foreground">
                            Gender
                          </TableHead>
                        ) : null}
                        <TableHead className="w-[72px] text-right z-label uppercase tracking-wide text-muted-foreground">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((user) => (
                        <TableRow
                          key={user.id}
                          className="bg-card transition-colors hover:bg-muted/20"
                        >
                          <TableCell className="py-3">
                            <button
                              type="button"
                              onClick={() => openPatient(user.id)}
                              className="group flex w-full items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            >
                              <Avatar className="h-10 w-10 rounded-2xl border border-border/70">
                                <AvatarImage
                                  src={user.profile_pic || undefined}
                                  alt={getPatientName(user)}
                                />
                                <AvatarFallback className="rounded-2xl bg-muted text-sm font-semibold text-foreground">
                                  {getPatientInitials(user)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="min-w-0 space-y-1">
                                <p className="truncate z-body-md text-foreground transition-colors group-hover:text-primary">
                                  {getPatientName(user)}
                                </p>
                              </div>
                            </button>
                          </TableCell>

                          {visibleColumns.has("patientNumber") ? (
                            <TableCell className="hidden md:table-cell py-3">
                              {user.zarity_user_id ? (
                                <Badge
                                  variant="outline"
                                  className="rounded-full border-border/70 bg-background/80 px-2.5 py-1 z-caption z-numeric shadow-none"
                                >
                                  {user.zarity_user_id}
                                </Badge>
                              ) : (
                                <span className="z-caption text-muted-foreground">
                                  Not assigned
                                </span>
                              )}
                            </TableCell>
                          ) : null}

                          {visibleColumns.has("contact") ? (
                            <TableCell className="py-3">
                              <div className="space-y-1">
                                <p className="truncate z-body text-foreground">
                                  {maskEmail(user.email)}
                                </p>
                                <p className="z-caption z-numeric text-muted-foreground">
                                  {maskPhoneNumber(user.phone_number)}
                                </p>
                              </div>
                            </TableCell>
                          ) : null}

                          {visibleColumns.has("city") ? (
                            <TableCell className="hidden lg:table-cell py-3 z-body text-foreground">
                              {user.city || "Not set"}
                            </TableCell>
                          ) : null}

                          {visibleColumns.has("age") ? (
                            <TableCell className="hidden xl:table-cell py-3 z-body text-foreground">
                              {user.age != null ? `${user.age}` : "—"}
                            </TableCell>
                          ) : null}

                          {visibleColumns.has("gender") ? (
                            <TableCell className="hidden xl:table-cell py-3">
                              <Badge
                                variant="outline"
                                className="rounded-full border-border/70 bg-background/80 px-2.5 py-1 z-caption shadow-none"
                              >
                                {formatGender(user.gender)}
                              </Badge>
                            </TableCell>
                          ) : null}

                          <TableCell className="py-3">
                            <div className="flex justify-end">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-9 w-9 rounded-xl border-border/70 bg-background/80 text-muted-foreground shadow-none"
                                    aria-label={`Open actions for ${getPatientName(user)}`}
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  className="w-44"
                                >
                                  <DropdownMenuItem
                                    onClick={() =>
                                      openPatient(user.id, "overview")
                                    }
                                  >
                                    <UserRound className="h-4 w-4" />
                                    Overview
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() =>
                                      openPatient(user.id, "profile-intake")
                                    }
                                  >
                                    <ClipboardList className="h-4 w-4" />
                                    Profile intake
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() =>
                                      openPatient(user.id, "patient-records")
                                    }
                                  >
                                    <FileText className="h-4 w-4" />
                                    Patient records
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() =>
                                      openPatient(user.id, "comms")
                                    }
                                  >
                                    <MessageSquare className="h-4 w-4" />
                                    Communication
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
